import { useDemo, useOrg } from '../api/app-context';
import { IfWrites } from '../components/write-access';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { OrganisationListEntry, OrganisationListResponse } from '@ni/shared';
import { useOrganisations } from '../api/hooks';
import { errorLine } from '../api/errors';
import { OrganisationsTable } from '../components/organisations/organisations-table';
import { AccountChoice } from './accounts';
import { ErrorLine } from '../components/ui/error-line';
import { WorkingButton } from '../components/ui/working-button';
import { SEARCH_DEBOUNCE_MS, useSettled } from '../lib/debounce';
import { orgHasProtectionAccounts } from '../lib/vertical';
import {
  LOADING_MORE,
  LOAD_MORE,
  ORGANISATIONS_EMPTY,
  ORGANISATIONS_NEW_ACTION,
  ORGANISATIONS_NONE_MATCH,
  ORGANISATIONS_SEARCH_LABEL,
  ORGANISATIONS_SEARCH_PLACEHOLDER,
  ORGANISATIONS_TITLE,
} from '../lib/copy';
import { Loading } from '../components/ui/loading';
import { EmptyLine } from '../components/ui/empty-line';

/**
 * **The Organisations screen** (organisations-as-subjects note §1 R8): the
 * office's own roster of the organisations it keeps a record of.
 *
 * One control, where People has three. There is no status to chip — neither
 * vertical has a word for an organisation — and no order to choose, so what is
 * left is the name search, settled a quarter of a second after the last
 * keystroke exactly as the other two search boxes on this app settle.
 *
 * **Tracked only, and that is not a filter.** People carries a level because a
 * known person is a row on it: somebody research minted is somebody the office
 * may want to promote, so they are findable there. An organisation the office
 * does not track is findable too — in the knowledge base, which is the
 * every-kind surface and where Track is offered. There is nothing here to widen
 * this list to, which is why it has no Tracked / Known control and no line
 * under it about the ones it is not showing.
 *
 * Paging resets on its own when the search moves, and that is a property of the
 * key rather than a line of code: the settled word is the parameter is the query
 * key, so a different search is a different read that begins at its own first
 * page.
 */
export function Organisations() {
  const demo = useDemo();
  const org = useOrg();
  const [typed, setTyped] = useState('');
  const [q, search] = useSettled('', SEARCH_DEBOUNCE_MS);

  /* The account chip lives on the address rather than in state, for the reason
     the People screen's does: it is arrived at from the account page, so it has
     to survive being pasted. Read only in a security org — every other vertical
     refuses the parameter. */
  const [params, setParams] = useSearchParams();
  const security = orgHasProtectionAccounts(org);
  const accountId = security ? params.get('account_id') : null;

  /* Trimmed to nothing is the bare list, not a search for a space — and the
     empty string is dropped rather than sent, so the opening read and a cleared
     box are one cache entry. */
  const asked = q.trim();
  const organisations = useOrganisations({
    ...(asked.length > 0 ? { q: asked } : {}),
    ...(accountId ? { account_id: accountId } : {}),
  });

  const entries = flattenOrganisations(organisations.data?.pages);

  return (
    <div className="column">
      <div className="head">
        <div className="titles">
          <div className="title">{ORGANISATIONS_TITLE}</div>
        </div>
        <div className="actions-right">
          <IfWrites>
            <Link className="btn hero" to="/organizations/new">
              {ORGANISATIONS_NEW_ACTION}
            </Link>
          </IfWrites>
        </div>
      </div>

      {security ? (
        <AccountChoice
          value={accountId ?? ''}
          empty="All accounts"
          onChange={(id) => {
            const next = new URLSearchParams(params);
            if (id) next.set('account_id', id);
            else next.delete('account_id');
            setParams(next);
          }}
        />
      ) : null}

      <OrganisationControls
        typed={typed}
        onTyped={(next) => {
          setTyped(next);
          search(next);
        }}
      />

      <div className="section">
        {/* **Rows first, and a failure reported where it happened** — the order
            the People screen arrived at the hard way. A refused Load more must
            not replace the pages that did load with a single line: those rows
            are still true, and throwing them away loses the reader's place and
            wildly overstates what went wrong. So `isError` decides only what is
            said in the foot, and can empty the screen only when the screen was
            empty anyway, which is the first page failing. */}
        {organisations.isPending ? (
          <EmptyLine>
            <Loading />
          </EmptyLine>
        ) : entries.length === 0 ? (
          <EmptyLine>
            {demo && !organisations.isError && !asked
              ? 'No stored organizations.'
              : organisationsEmptyLine(asked, organisations.isError ? organisations.error : null)}
          </EmptyLine>
        ) : (
          <>
            <OrganisationsTable entries={entries} account={security} />
            {organisations.hasNextPage || organisations.isError ? (
              <div className="list-foot">
                {organisations.hasNextPage ? (
                  <WorkingButton
                    label={LOAD_MORE}
                    workingLabel={LOADING_MORE}
                    working={organisations.isFetchingNextPage}
                    variant="act"
                    tiny
                    onClick={() => void organisations.fetchNextPage()}
                  />
                ) : null}
                {organisations.isError ? <ErrorLine error={organisations.error} load /> : null}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * **Which of the three ways an empty list is empty.**
 *
 * A rule rather than three nested ternaries in the markup, because it is the one
 * thing on this screen a reader would be misled by: an org that tracks nothing
 * *yet* is at a beginning, a search that matched none is a search over a roster
 * that may be full, and a first page that failed is neither. Said here so the
 * three are asserted as the rule they are, rather than through a render whose
 * search settles a quarter of a second after the case has finished.
 */
export function organisationsEmptyLine(asked: string, error: unknown): string {
  if (error) return errorLine(error);
  return asked.length > 0 ? ORGANISATIONS_NONE_MATCH : ORGANISATIONS_EMPTY;
}

/** The loaded pages as one list — the roster's own `flattenPages`. */
export function flattenOrganisations(
  pages: readonly OrganisationListResponse[] | undefined,
): OrganisationListEntry[] {
  return pages ? pages.flatMap((page) => page.entries) : [];
}

/**
 * The one control, given its value rather than owning it — the split People and
 * the knowledge base both keep, and the reason what is on the page can be
 * asserted without a DOM.
 */
export function OrganisationControls({
  typed,
  onTyped,
}: {
  /** The live text, which is what the box shows; `q` is what it settles to. */
  typed: string;
  onTyped: (next: string) => void;
}) {
  return (
    <div className="people-controls">
      <div className="field search-field">
        <label className="label" htmlFor="organizations-q">
          {ORGANISATIONS_SEARCH_LABEL}
        </label>
        {/* Every name in this box is an organisation's, so the browser's saved
            values have nothing to offer — the same call both other search boxes
            in this app make. */}
        <input
          id="organizations-q"
          type="search"
          autoComplete="off"
          placeholder={ORGANISATIONS_SEARCH_PLACEHOLDER}
          value={typed}
          onChange={(event) => onTyped(event.target.value)}
        />
      </div>
    </div>
  );
}
